import { Angle, Triangle } from '@ritabygglov/core';
import { MIN_VIEW_SCALE } from '../../../constants';
import {
  bisectorAnchor,
  computeTriangleAngles,
  computeTriangleVertices,
  cornerArcPath,
} from '../../../helpers/triangleGeometry';
import { formatNumberOneDecimal } from '../../../helpers/utility';

type CornerAnglesProps = {
  element: Triangle | Angle;
  scale: number;
};

export const CornerAngles = ({ element, scale }: CornerAnglesProps) => {
  if (element.width === 0 || element.height === 0) return null;

  const vertices = computeTriangleVertices(
    element.pointRatio,
    element.width,
    element.height
  );
  const angles = computeTriangleAngles(vertices);
  if (!angles) return null;

  const { apex, baseLeft, baseRight } = vertices;
  const { x, y } = element;

  const ANNOTATION_SCALE_MAX = 0.4;

  const cappedScale = Math.min(
    ANNOTATION_SCALE_MAX,
    Math.max(MIN_VIEW_SCALE, scale)
  );

  // suitable constant values chosen
  const perpendicularOffset = 60 * cappedScale;
  const arcRadius = 40 * cappedScale;
  const fontSize = 16 * cappedScale;
  const arcStrokeWidth = 1.2 * cappedScale;

  const apexAnchor = bisectorAnchor(
    apex,
    baseLeft,
    baseRight,
    angles.apex,
    perpendicularOffset
  );
  const baseLeftAnchor = bisectorAnchor(
    baseLeft,
    apex,
    baseRight,
    angles.baseLeft,
    perpendicularOffset
  );
  const baseRightAnchor = bisectorAnchor(
    baseRight,
    apex,
    baseLeft,
    angles.baseRight,
    perpendicularOffset
  );

  if (!apexAnchor || !baseLeftAnchor || !baseRightAnchor) return null;

  const arcs: Array<{ d: string | null; key: string }> = [
    {
      d: cornerArcPath(apex, baseLeft, baseRight, arcRadius),
      key: 'apex',
    },
    {
      d: cornerArcPath(baseLeft, apex, baseRight, arcRadius),
      key: 'baseLeft',
    },
    {
      d: cornerArcPath(baseRight, apex, baseLeft, arcRadius),
      key: 'baseRight',
    },
  ];

  const labels: Array<{
    anchor: [number, number];
    value: number;
    key: string;
  }> = [
    { anchor: apexAnchor, value: angles.apex, key: 'apex' },
    { anchor: baseLeftAnchor, value: angles.baseLeft, key: 'baseLeft' },
    { anchor: baseRightAnchor, value: angles.baseRight, key: 'baseRight' },
  ];

  return (
    <g data-testid="corner-angles">
      {arcs.map(({ d, key }) =>
        d ? (
          <path
            key={`arc-${key}`}
            d={d}
            fill="none"
            stroke="var(--active-blue)"
            strokeWidth={arcStrokeWidth}
            opacity={0.6}
            transform={`translate(${x} ${y})`}
          />
        ) : null
      )}
      {labels.map(({ anchor, value, key }) => (
        <text
          key={key}
          fill="var(--active-blue)"
          opacity={0.85}
          fontSize={fontSize}
          x={x + anchor[0]}
          y={y + anchor[1]}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {formatNumberOneDecimal(value)}°
        </text>
      ))}
    </g>
  );
};
